import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { ProjectService } from '../../../../shared/services/project.service';
import { ProjectStage } from '../../../../shared/models/project.model';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { PreventScrollPropagationDirective } from '../../../../shared/directives/prevent-scroll-propagation.directive';

// ADD PHASE FOR A PROJECT MODAL
@Component({
  selector: 'app-add-stage-project',
  imports: [FormsModule, CommonModule, DateInputsModule, PreventScrollPropagationDirective],
  templateUrl: './add-stage-project.html'
})
export class AddStageProjectComponent {
  @Output() close = new EventEmitter<void>();
  @Input() preselectedProjectId: string | null = null;
  @Input() projectStartDate?: string;
  @Input() projectBudget: number = 0;
  @Input() existingPhaseHours: number = 0;
  private projectService = inject(ProjectService);
  isSubmitting = false;
  statusList = ["NOTBEGIN", "INPROGRESS", "DONE"];

  // For date constraints as Date objects for Kendo DatePicker
  minDate: Date = new Date();
  startDateValue: Date = new Date();
  endDateValue: Date = new Date();

  projectStageData: ProjectStage = {
    startDate: "",
    endDate: "",
    stageName: '',
    color: '#6366f1',
    status: "NOTBEGIN",
    projectId: "",
    estimatedHours: 0
  };

  estimatedTimeInput: string | number = '';

  getParsedHours(): number {
    if (!this.estimatedTimeInput) return 0;
    const val = parseFloat(String(this.estimatedTimeInput).replace(',', '.'));
    return isNaN(val) ? 0 : val;
  }

  isOverBudget(): boolean {
    if (!this.projectBudget || this.projectBudget <= 0) return false;
    return (this.existingPhaseHours + this.getParsedHours()) > this.projectBudget;
  }

  getRemainingHours(): number {
    return this.projectBudget - this.existingPhaseHours - this.getParsedHours();
  }

  statusOptions = [
    { text: 'New', value: 'NOTBEGIN' },
    { text: 'In Progress', value: 'INPROGRESS' },
    { text: 'Done', value: 'DONE' }
  ];

  ngOnInit() {
    if (this.preselectedProjectId) this.projectStageData.projectId = this.preselectedProjectId;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Pre-configure dates based on project start date
    if (this.projectStartDate) {
      // Parse the date string as yyyy-MM-dd and create Date object in local timezone
      const parts = this.projectStartDate.split('-');
      const projStart = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
      projStart.setHours(0, 0, 0, 0);
      this.minDate = projStart;
      this.startDateValue = new Date(projStart);
    } else {
      // No project start date provided, use today
      this.minDate = today;
      this.startDateValue = new Date(today);
    }

    // Use local date to avoid timezone issues
    const startYear = this.startDateValue.getFullYear();
    const startMonth = String(this.startDateValue.getMonth() + 1).padStart(2, '0');
    const startDay = String(this.startDateValue.getDate()).padStart(2, '0');
    this.projectStageData.startDate = `${startYear}-${startMonth}-${startDay}`;

    // Default end date to 30 days from start
    this.endDateValue = new Date(this.startDateValue);
    this.endDateValue.setDate(this.endDateValue.getDate() + 30);
    const endYear = this.endDateValue.getFullYear();
    const endMonth = String(this.endDateValue.getMonth() + 1).padStart(2, '0');
    const endDay = String(this.endDateValue.getDate()).padStart(2, '0');
    this.projectStageData.endDate = `${endYear}-${endMonth}-${endDay}`;
  }

  // Handle start date change from Kendo DatePicker
  onStartDateChange(value: Date | null) {
    if (value) {
      this.startDateValue = value;
      // Use local date to avoid timezone issues
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      this.projectStageData.startDate = `${year}-${month}-${day}`;

      // Update end date if it's before start date
      if (this.endDateValue < value) {
        this.endDateValue = new Date(value);
        this.endDateValue.setDate(this.endDateValue.getDate() + 30);
        const endYear = this.endDateValue.getFullYear();
        const endMonth = String(this.endDateValue.getMonth() + 1).padStart(2, '0');
        const endDay = String(this.endDateValue.getDate()).padStart(2, '0');
        this.projectStageData.endDate = `${endYear}-${endMonth}-${endDay}`;
      }
    }
  }

  // Handle end date change from Kendo DatePicker
  onEndDateChange(value: Date | null) {
    if (value) {
      this.endDateValue = value;
      // Use local date to avoid timezone issues
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      this.projectStageData.endDate = `${year}-${month}-${day}`;
    }
  }

  async onSubmit() {
    if (!this.projectStageData.projectId) {
      alert('Project ID is required');
      return;
    }
    this.isSubmitting = true;
    try {
      let parsedEstimatedTime = 0;
      if (this.estimatedTimeInput) {
        const timeStr = String(this.estimatedTimeInput).replace(',', '.');
        parsedEstimatedTime = parseFloat(timeStr);
        if (isNaN(parsedEstimatedTime)) {
          parsedEstimatedTime = 0;
        }
      }

      const projectStage: ProjectStage = {
        projectId: this.projectStageData.projectId,
        stageName: this.projectStageData.stageName,
        color: this.projectStageData.color,
        startDate: this.projectStageData.startDate,
        endDate: this.projectStageData.endDate,
        status: this.projectStageData.status,
        estimatedHours: parsedEstimatedTime
      }
      await this.projectService.createProjectStage(projectStage);
      this.projectStageData.stageName = '';
      this.projectStageData.status = 'NOTBEGIN';
      this.estimatedTimeInput = '';
      this.close.emit();
    } catch (err) {
      console.error("Error creating project stage:", err);
      alert("Error while creating project stage.");
    } finally {
      this.isSubmitting = false;
    }
  }

  getStatusText(value:  string | null): string {
    const status = this.statusOptions.find(s => s.value === value);
    return status ? status.text : '';
  }
}
